import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { CaretDown, CaretRight, Storefront, PencilSimple } from '@phosphor-icons/react';
import tableStyles from '../../styles/tables.module.css';
import styles from './BOQTable.module.css';
import { UNIT_MAP, formatSelectedOption } from '../../utils/boqHelpers';
import { useData } from '../../hooks/useData';
import { TABLES } from '../../constants';

// Get the effective cost for a project item (pricing waterfall: quote > override > estimate)
function getEffectiveCost(item) {
  return Number(item.cost_override || item.estimated_cost || 0);
}

// Merge items with same master_item_id + selected_option, summing quantities and costs
function mergeItems(items) {
  const mergeMap = new Map();
  items.forEach(item => {
    // Custom items (no master_item_id) are never merged
    if (!item.master_item_id) {
      mergeMap.set(item.id, item);
      return;
    }
    const optionKey = JSON.stringify(item.selected_option || {});
    const mergeKey = `${item.master_item_id}__${optionKey}`;
    if (mergeMap.has(mergeKey)) {
      const existing = mergeMap.get(mergeKey);
      // Build space breakdown before summing (so we capture per-source quantities)
      if (!existing._spaceBreakdown) {
        // First merge — record the original item's space + qty
        existing._spaceBreakdown = [
          { spaceId: existing.space_id, qty: Number(existing._originalQty) || 1 },
        ];
      }
      existing._spaceBreakdown.push({ spaceId: item.space_id, qty: Number(item.quantity) || 1 });
      // Sum quantities and costs
      existing.quantity = (Number(existing.quantity) || 1) + (Number(item.quantity) || 1);
      existing.estimated_cost =
        (Number(existing.estimated_cost) || 0) + (Number(item.estimated_cost) || 0);
      existing.cost_override =
        existing.cost_override && item.cost_override
          ? Number(existing.cost_override) + Number(item.cost_override)
          : existing.cost_override || item.cost_override || null;
      // Track all source item IDs for vendor lookup
      existing._sourceIds = [...(existing._sourceIds || [existing.id]), item.id];
    } else {
      // Clone to avoid mutating original — store _originalQty for first-merge breakdown
      mergeMap.set(mergeKey, { ...item, _sourceIds: [item.id], _originalQty: item.quantity });
    }
  });
  return Array.from(mergeMap.values());
}

// Row for a project item in the BOQ table
function ItemRow({ item, costTypes, bbChapters, vendorMap, spaceMap, onUpdateNotes }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSpaceTooltip, setShowSpaceTooltip] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [localNotes, setLocalNotes] = useState('');
  const popoverRef = useRef(null);
  const textareaRef = useRef(null);

  // Close popover on click outside
  useEffect(() => {
    if (!isEditingNotes) return;
    const handleClickOutside = e => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsEditingNotes(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingNotes]);

  // Focus textarea when popover opens
  useEffect(() => {
    if (isEditingNotes && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditingNotes]);

  const handleOpenNotes = useCallback(() => {
    setLocalNotes(item.details?.notes || '');
    setIsEditingNotes(true);
  }, [item.details?.notes]);

  const handleSaveNotes = useCallback(() => {
    const currentNotes = item.details?.notes || '';
    if (localNotes !== currentNotes) {
      onUpdateNotes(item, localNotes);
    }
    setIsEditingNotes(false);
  }, [localNotes, item, onUpdateNotes]);

  // Resolve display values
  const itemName = item.name_override || item.master_items?.item_name || '—';
  const optionText = formatSelectedOption(item.selected_option);
  const notes = item.details?.notes || '';
  const truncatedNotes = notes.length > 30 ? notes.substring(0, 30) + '...' : notes;
  // For custom items (no master_item), cost_type_id is stored in details JSONB
  const costTypeId = item.master_items?.cost_type_id || item.details?.cost_type_id;
  const costType = costTypes?.find(ct => ct.id === costTypeId);
  const unitLabel = costType ? UNIT_MAP[costType.name] || costType.name : '—';
  const effectiveCost = getEffectiveCost(item);
  // Check all source IDs for vendor (merged items may have multiple source IDs)
  const sourceIds = item._sourceIds || [item.id];
  const vendor = sourceIds.reduce((found, id) => found || vendorMap?.get(id), null);

  // BB chapter info — from master_items for linked items, from project_items.bb_chapter_id for custom
  const chapterId = item.master_items?.bb_chapter_id || item.bb_chapter_id;
  const chapter = chapterId ? bbChapters?.find(ch => ch.id === chapterId) : null;

  return (
    <>
      <tr className={tableStyles.row}>
        {/* Expand/collapse button */}
        <td className={tableStyles.cell} style={{ width: '32px' }}>
          <button
            className={tableStyles.expandButton}
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
          >
            {isExpanded ? <CaretDown size={18} /> : <CaretRight size={18} />}
          </button>
        </td>
        {/* BB chapter code */}
        <td className={tableStyles.cell} style={{ width: '70px' }}>
          {chapter?.code || '—'}
        </td>
        {/* BB chapter name */}
        <td className={tableStyles.cell} style={{ width: '100px' }}>
          {chapter?.name_he || '—'}
        </td>
        {/* Item name + option details */}
        <td className={tableStyles.cell} style={{ fontWeight: 500 }}>
          {itemName}
          {optionText && (
            <div style={{ fontWeight: 400, fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
              {optionText}
            </div>
          )}
        </td>
        {/* Description / notes — click to edit */}
        <td className={tableStyles.cell} style={{ position: 'relative' }}>
          <div className={styles.descriptionWrapper} ref={isEditingNotes ? popoverRef : undefined}>
            <div className={styles.descriptionDisplay} onClick={handleOpenNotes} title={notes}>
              <span style={{ color: notes ? '#64748b' : '#94a3b8', fontSize: '13px' }}>
                {truncatedNotes || '—'}
              </span>
              <PencilSimple size={14} className={styles.editIcon} />
            </div>
            {isEditingNotes && (
              <div className={styles.descriptionPopover}>
                <textarea
                  ref={textareaRef}
                  className={styles.descriptionTextarea}
                  value={localNotes}
                  onChange={e => setLocalNotes(e.target.value)}
                  placeholder="הוסף תיאור..."
                  dir="rtl"
                />
                <div className={styles.popoverActions}>
                  <button className={styles.popoverSave} onClick={handleSaveNotes}>
                    שמור
                  </button>
                </div>
              </div>
            )}
          </div>
        </td>
        {/* Unit */}
        <td className={tableStyles.cell} style={{ width: '180px', minWidth: '180px' }}>
          {unitLabel}
        </td>
        {/* Quantity — with space breakdown popup on hover */}
        <td
          className={tableStyles.cell}
          style={{ textAlign: 'center', position: 'relative' }}
          onMouseEnter={() => setShowSpaceTooltip(true)}
          onMouseLeave={() => setShowSpaceTooltip(false)}
        >
          <span style={{ cursor: 'help', borderBottom: '1px dashed #94a3b8' }}>
            {item.quantity || 1}
          </span>
          {showSpaceTooltip && (
            <div className={styles.spaceTooltip}>
              {(
                item._spaceBreakdown || [
                  { spaceId: item.space_id, qty: Number(item._originalQty || item.quantity) || 1 },
                ]
              ).map((sb, i) => {
                const name = sb.spaceId ? spaceMap?.get(sb.spaceId) || 'חלל' : 'ללא חלל';
                return (
                  <div key={i}>
                    {name}: {sb.qty}
                  </div>
                );
              })}
            </div>
          )}
        </td>
        {/* Vendor */}
        <td className={tableStyles.cell} style={{ width: '100px' }}>
          {vendor ? (
            <div className={styles.vendorBadge}>
              <Storefront size={14} />
              <span>{vendor.name || 'ספק'}</span>
            </div>
          ) : (
            <span className={styles.noVendor}>—</span>
          )}
        </td>
        {/* Actions placeholder (read-only for now) */}
        <td className={tableStyles.cell} style={{ width: '80px' }}></td>
      </tr>
      {/* Expanded row: cost details */}
      {isExpanded && (
        <tr className={tableStyles.row}>
          <td colSpan="9" className={tableStyles.expandedContent}>
            <div className={styles.expandedContent}>
              <div className={styles.expandedFields}>
                <div className={styles.fieldGroup}>
                  <label>עלות משוערת</label>
                  <div className={styles.totalCost}>
                    {item.estimated_cost ? `₪${Number(item.estimated_cost).toLocaleString()}` : '—'}
                  </div>
                </div>
                <div className={styles.fieldGroup}>
                  <label>עלות מותאמת</label>
                  <div className={styles.totalCost}>
                    {item.cost_override ? `₪${Number(item.cost_override).toLocaleString()}` : '—'}
                  </div>
                </div>
                <div className={styles.fieldGroup}>
                  <label>סה&quot;כ</label>
                  <div className={styles.totalCost}>
                    {effectiveCost > 0 ? `₪${Math.round(effectiveCost).toLocaleString()}` : '—'}
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function BOQTable({
  projectItemRows = [],
  costTypes = [],
  bbChapters = [],
  vendorMap = new Map(),
  spaceMap = new Map(),
  isLoading,
}) {
  const { update } = useData(TABLES.PROJECT_ITEMS);
  const NO_CHAPTER_KEY = '__no_chapter__';

  // Save notes to project_items.details JSONB (for all source IDs on merged rows)
  const handleUpdateNotes = useCallback(
    async (item, newNotes) => {
      const sourceIds = item._sourceIds || [item.id];
      for (const id of sourceIds) {
        const currentDetails = item.details || {};
        await update({
          id,
          data: { details: { ...currentDetails, notes: newNotes } },
        });
      }
    },
    [update],
  );

  // Build a lookup map for chapters by id
  const chapterById = useMemo(() => {
    const map = new Map();
    bbChapters?.forEach(ch => map.set(ch.id, ch));
    return map;
  }, [bbChapters]);

  // Walk up parent_id chain to find the top-level (depth=0) ancestor chapter
  const getTopLevelChapter = chapterId => {
    if (!chapterId || chapterId === NO_CHAPTER_KEY) return null;
    let current = chapterById.get(chapterId);
    if (!current) return null;
    // Walk up until we reach a root (no parent or depth=0)
    while (current.parent_id && chapterById.get(current.parent_id)) {
      current = chapterById.get(current.parent_id);
    }
    return current;
  };

  // Merge duplicate items (same master_item_id + selected_option) before grouping
  const mergedRows = useMemo(() => mergeItems(projectItemRows), [projectItemRows]);

  // Group merged items by top-level BB chapter
  const groupedItems = useMemo(() => {
    if (!mergedRows?.length) return [];

    const groupMap = new Map();

    mergedRows.forEach(item => {
      // Chapter comes from master_items (linked items) or project_items.bb_chapter_id (custom items)
      const itemChapterId =
        item.master_items?.bb_chapter_id || item.bb_chapter_id || NO_CHAPTER_KEY;
      const topChapter = getTopLevelChapter(itemChapterId);
      const groupKey = topChapter?.id || NO_CHAPTER_KEY;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          chapterId: groupKey,
          chapterCode: topChapter?.code || '',
          chapterName: topChapter?.name_he || 'ללא פרק',
          items: [],
        });
      }
      groupMap.get(groupKey).items.push(item);
    });

    // Sort items within each group by their sub-chapter code
    groupMap.forEach(group => {
      group.items.sort((a, b) => {
        const aChapterId = a.master_items?.bb_chapter_id || a.bb_chapter_id;
        const bChapterId = b.master_items?.bb_chapter_id || b.bb_chapter_id;
        const aCode = aChapterId ? chapterById.get(aChapterId)?.code || '' : '';
        const bCode = bChapterId ? chapterById.get(bChapterId)?.code || '' : '';
        return aCode.localeCompare(bCode, undefined, { numeric: true });
      });
    });

    // Sort groups: chapters with codes first (sorted by code), then "no chapter" last
    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.chapterId === NO_CHAPTER_KEY) return 1;
      if (b.chapterId === NO_CHAPTER_KEY) return -1;
      return a.chapterCode.localeCompare(b.chapterCode, undefined, { numeric: true });
    });
  }, [mergedRows, bbChapters, chapterById]);

  if (isLoading) {
    return (
      <div className={tableStyles.loadingContainer}>
        <div className={tableStyles.spinner} />
      </div>
    );
  }

  if (!mergedRows?.length) {
    return <div className={tableStyles.emptyState}>אין פריטים</div>;
  }

  return (
    <div className={tableStyles.tableContainer}>
      <div className={tableStyles.tableScrollWrapper}>
        <table className={tableStyles.table}>
          <thead>
            <tr className={tableStyles.headerRow}>
              <th className={tableStyles.headerCell} style={{ width: '32px' }}></th>
              <th className={tableStyles.headerCell} style={{ width: '70px' }}>
                מספר סעיף
              </th>
              <th className={tableStyles.headerCell} style={{ width: '100px' }}>
                סעיף
              </th>
              <th className={tableStyles.headerCell} style={{ width: '140px' }}>
                פריט
              </th>
              <th className={tableStyles.headerCell} style={{ minWidth: '200px' }}>
                תיאור
              </th>
              <th className={tableStyles.headerCell} style={{ width: '180px', minWidth: '180px' }}>
                יחידה
              </th>
              <th className={tableStyles.headerCell} style={{ width: '60px' }}>
                כמות
              </th>
              <th className={tableStyles.headerCell} style={{ width: '100px' }}>
                ספק
              </th>
              <th className={tableStyles.headerCell} style={{ width: '80px' }}></th>
            </tr>
          </thead>
          <tbody>
            {groupedItems.map(group => {
              // Sum effective costs for the chapter group
              const groupTotal = group.items.reduce((sum, item) => sum + getEffectiveCost(item), 0);

              return (
                <React.Fragment key={group.chapterId}>
                  {/* Chapter Group Header */}
                  <tr className={tableStyles.categoryHeader}>
                    <td colSpan="9">
                      {group.chapterCode && `${group.chapterCode} - `}
                      {group.chapterName}
                      {groupTotal > 0 && (
                        <span style={{ color: '#64748b', fontWeight: 500, marginRight: '8px' }}>
                          (סה&quot;כ ₪{Math.round(groupTotal).toLocaleString()})
                        </span>
                      )}
                    </td>
                  </tr>
                  {/* Project items in this chapter */}
                  {group.items.map(item => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      costTypes={costTypes}
                      bbChapters={bbChapters}
                      vendorMap={vendorMap}
                      spaceMap={spaceMap}
                      onUpdateNotes={handleUpdateNotes}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
